import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  BarChart2,
  Bot,
  ChevronRight,
  Clock,
  Filter,
  Hand,
  Info,
  RotateCcw,
  Shield,
  Target,
  TrendingDown,
  TrendingUp,
  XCircle,
  Zap,
} from "lucide-react";
import { formatUSDT } from "@/lib/mockData";
import {
  formatEtherWithDecimals,
  formatKMBTruncate,
  formatNumberTruncate,
} from "@/lib/utils";
import OrderDetailDialog from "@/components/aiTrading/OrderDetailDialog";
import ClosePositionDialog from "@/components/aiTrading/ClosePositionDialog";
import HistoryDetailDialog, {
  type HistoryOrderView,
} from "@/components/aiTrading/HistoryDetailDialog";
import { useGetOpenOrderList } from "@/hooks/api/useGetOpenOrderList";
import { useGetClosedOrderList } from "@/hooks/api/useGetClosedOrderList";
import { useGetUserCexOrderTradeStats } from "@/hooks/api/useGetUserCexOrderTradeStats";
import type { RunningOrderView } from "@/components/aiTrading/orderViewTypes";
import type { CexRunningOrderListItem, ClosedOrderMode } from "@/api/types";
import { useTranslation } from "react-i18next";
import { getTokenName, INTEGRAL_COIN_CONTRACT } from "@/config/common";

const C = {
  card: "rgba(15,17,23,0.75)",
  border: "rgba(255,255,255,0.07)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  dim: "#64748b",
  primary: "#22c55e",
  green: "#22c55e",
  red: "#ef4444",
  amber: "#f59e0b",
  blue: "#3b82f6",
  cyan: "#06b6d4",
};

const COIN_LOGOS: Record<string, string> = {
  BTC: "https://cryptologos.cc/logos/bitcoin-btc-logo.svg",
  ETH: "https://cryptologos.cc/logos/ethereum-eth-logo.svg",
  BNB: "https://cryptologos.cc/logos/bnb-bnb-logo.svg",
  SOL: "https://cryptologos.cc/logos/solana-sol-logo.svg",
};

function CoinLogo({ coin, size = 18 }: { coin: string; size?: number }) {
  const src = COIN_LOGOS[coin];
  if (!src) {
    return (
      <span className="font-bold" style={{ fontSize: size * 0.7 }}>
        {coin[0]}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={coin}
      style={{ width: size, height: size }}
      className="rounded-full"
    />
  );
}

function DirBadge({ dir }: { dir: string }) {
  const { t } = useTranslation();
  const isLong = dir === "多";
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold mono-num"
      style={{
        background: isLong ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
        color: isLong ? C.green : C.red,
        border: `1px solid ${isLong ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
      }}
    >
      {isLong ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {isLong ? t("aiTradingPage.badgeLong") : t("aiTradingPage.badgeShort")}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation();
  const isAuto = source === "auto";
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium"
      style={{
        background: isAuto ? "rgba(34,197,94,0.10)" : "rgba(59,130,246,0.10)",
        color: isAuto ? C.green : C.blue,
        border: `1px solid ${isAuto ? "rgba(34,197,94,0.20)" : "rgba(59,130,246,0.20)"}`,
      }}
    >
      {isAuto ? <Bot size={9} /> : <Hand size={9} />}
      {isAuto ? t("aiTradingPage.badgeAuto") : t("aiTradingPage.badgeManual")}
    </span>
  );
}

function HoldingTimerInline({ openTimeMs }: { openTimeMs: number }) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState("");
  useEffect(() => {
    const openDate = new Date(openTimeMs);
    const update = () => {
      const now = Date.now();
      const diff = now - openDate.getTime();
      if (diff < 0) {
        setElapsed(t("aiTradingPage.holdDurZero"));
        return;
      }
      const totalSec = Math.floor(diff / 1000);
      const totalMin = Math.floor(totalSec / 60);
      const d = Math.floor(totalMin / 1440);
      const h = Math.floor((totalMin % 1440) / 60);
      const m = totalMin % 60;
      const s = totalSec % 60;

      // 1 小时内展示到秒；1 小时及以上仅展示到分钟
      if (totalSec < 3600) {
        setElapsed(
          `${m}${t("aiTradingPage.holdDurMin")} ${t("aiTradingPage.durationSeconds", { count: s })}`
        );
        return;
      }

      let str = "";
      if (d > 0) str += `${d}${t("aiTradingPage.holdDurDay")} `;
      if (h > 0 || d > 0) str += `${h}${t("aiTradingPage.holdDurHour")} `;
      str += `${m}${t("aiTradingPage.holdDurMin")}`;
      setElapsed(str);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [openTimeMs, t]);

  return <>{elapsed}</>;
}

interface OrdersHistorySectionProps {
  coinPrices: Record<string, { price: number; change: number }>;
  onReorderFromHistory: (coin: string) => void;
}

// function getOpenOrderLivePnl({
//   type,
//   margin,
//   leverage,
//   entryPrice,
//   currentPrice,
// }: {
//   type: "多" | "空";
//   margin: number;
//   leverage: number;
//   entryPrice: number;
//   currentPrice: number;
// }) {
//   if (entryPrice <= 0 || margin <= 0 || leverage <= 0 || currentPrice <= 0) {
//     return 0;
//   }
//   const positionValue = margin * leverage;
//   const priceChangeRatio = (currentPrice - entryPrice) / entryPrice;
//   if (type === "多") {
//     return positionValue * priceChangeRatio;
//   }
//   return positionValue * -priceChangeRatio;
// }

export default function OrdersHistorySection({
  coinPrices,
  onReorderFromHistory,
}: OrdersHistorySectionProps) {
  const { t } = useTranslation();
  const tskxDisplayName = getTokenName(INTEGRAL_COIN_CONTRACT);
  void coinPrices;
  const {
    status: openOrderListStatus,
    data: openOrderList,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isOpenOrdersLoading,
  } = useGetOpenOrderList();
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeOrderId, setCloseOrderId] = useState<string | null>(null);
  const [historyDetailOrder, setHistoryDetailOrder] =
    useState<HistoryOrderView | null>(null);

  const [lowerTab, setLowerTab] = useState<"orders" | "history">("orders");
  const [historyFilter, setHistoryFilter] = useState<"all" | "auto" | "manual">(
    "all"
  );
  const closedOrderMode: ClosedOrderMode = historyFilter;
  const {
    status: closedOrderListStatus,
    data: closedOrderList,
    hasNextPage: hasClosedNextPage,
    fetchNextPage: fetchClosedNextPage,
    isFetchingNextPage: isFetchingClosedNextPage,
    isLoading: isClosedOrdersLoading,
    refetch: refetchClosedOrderList,
  } = useGetClosedOrderList(closedOrderMode);
  const {
    data: userCexOrderTradeStats,
    refetch: refetchUserCexOrderTradeStats,
  } = useGetUserCexOrderTradeStats();

  const runningOrders = useMemo<RunningOrderView[]>(() => {
    const items = openOrderList?.pages.flatMap(p => p.items ?? []) ?? [];
    return items.map((order: CexRunningOrderListItem) => {
      const coin = order.symbol.endsWith("USDT")
        ? order.symbol.slice(0, -4)
        : order.symbol;
      const isBuy = order.action === "buy";
      const type: "多" | "空" = isBuy ? "多" : "空";
      // const livePrice = coinPrices[coin]?.price ?? 0;
      // const currentPrice =
      //   livePrice > 0 ? livePrice : Math.max(order.openAmount, 0);
      // const livePnl = getOpenOrderLivePnl({
      //   type,
      //   margin: order.amount,
      //   leverage: order.leverage,
      //   entryPrice: order.openAmount,
      //   currentPrice,
      // });
      const tradeFeeUsdt = Number(
        formatEtherWithDecimals(order.tradeFee?.totalUsdtAmount ?? "0", 6)
      );
      const signalFeeUsdt = Number(
        formatEtherWithDecimals(order.signaleFee?.totalUsdtAmount ?? "0", 6)
      );
      // const netLivePnl = livePnl - tradeFeeUsdt - signalFeeUsdt;
      const netLivePnl = Number(order.pnl ?? 0) - tradeFeeUsdt - signalFeeUsdt;
      return {
        id: order.id,
        symbol: order.symbol,
        coin,
        type,
        orderSource: order.autoOrder ? "auto" : "manual",
        strategy: order.strategyId,
        margin: order.amount,
        leverage: order.leverage,
        riskId: Number(order.riskId ?? 0),
        pnl: netLivePnl,
        pnlDisplay: netLivePnl,
        openAmount: order.openAmount,
        takeProfit: order.takeProfit,
        stopLoss: order.stopLoss,
        tradeFeeTotalUsdtAmount: order.tradeFee?.totalUsdtAmount ?? "0",
        signalFeeTotalUsdtAmount: order.signaleFee?.totalUsdtAmount ?? "0",
        orderStaticRatio: Number(order.orderStaticRatio ?? 0),
        orderStaticOnlyURatio: Number(order.orderStaticOnlyURatio ?? 0),
        orderStaticU2TskRatio: Number(order.orderStaticU2TskRatio ?? 0),
        orderCloseLossTskxRatio: Number(order.orderCloseLossTskxRatio ?? 0),
        openTimeMs: order.createTime,
        openTime: new Date(order.createTime).toLocaleString(),
        tradeFeeRateBps: Number(order.tradeFeeRateBps ?? 0),
        signalFeeRateBps: Number(order.signalFeeRateBps ?? 0),
      };
    });
  }, [openOrderList]);

  const closeOrder = useMemo<RunningOrderView | null>(() => {
    if (!closeOrderId) return null;
    return runningOrders.find(order => order.id === closeOrderId) ?? null;
  }, [closeOrderId, runningOrders]);
  const detailOrder = useMemo<RunningOrderView | null>(() => {
    if (!detailOrderId) return null;
    return runningOrders.find(order => order.id === detailOrderId) ?? null;
  }, [detailOrderId, runningOrders]);

  const closedOrders = useMemo<HistoryOrderView[]>(() => {
    const items = closedOrderList?.pages.flatMap(p => p.items ?? []) ?? [];
    return items.map((order: CexRunningOrderListItem) => {
      const coin = order.symbol.endsWith("USDT")
        ? order.symbol.slice(0, -4)
        : order.symbol;
      const isBuy = order.action === "buy";
      const tradeFeeUsdt = Number(
        formatEtherWithDecimals(order.tradeFee?.totalUsdtAmount ?? "0", 6)
      );
      const signalFeeUsdt = Number(
        formatEtherWithDecimals(order.signaleFee?.totalUsdtAmount ?? "0", 6)
      );
      const netClosedPnl = order.closeProfit - tradeFeeUsdt - signalFeeUsdt;
      return {
        id: order.id,
        coin,
        type: isBuy ? "多" : "空",
        orderSource: order.autoOrder ? "auto" : "manual",
        strategy: order.strategyId,
        margin: order.amount,
        leverage: order.leverage,
        pnl: order.closeProfit + order.subsidyPaidUsdt,
        truthPnl: order.truthPnl,
        pnlDisplay: order.truthPnl,
        entryPrice: order.openAmount,
        // TODO：这里需要根据实际的平仓价格来计算
        closePrice: order.closeAmount,
        openTime: new Date(order.openTime).toLocaleString(),
        closeTime: order.closeTime
          ? new Date(order.closeTime).toLocaleString()
          : "--",
        closeSplitStaticUsdt: Number(order.closeSplitStaticUsdt ?? 0),
        closeSplitStaticOnlyUsdt: Number(order.closeSplitStaticOnlyUsdt ?? 0),
        closeSplitStaticTskU: Number(order.closeSplitStaticTskU ?? 0),
        closeSplitLossUserTskx: Number(order.closeSplitLossUserTskx ?? 0),
        orderStaticRatio: Number(order.orderStaticRatio ?? 0),
        orderStaticOnlyURatio: Number(order.orderStaticOnlyURatio ?? 0),
        orderStaticU2TskRatio: Number(order.orderStaticU2TskRatio ?? 0),
        orderCloseLossTskxRatio: Number(order.orderCloseLossTskxRatio ?? 0),
        tradeFeeTotalUsdtAmount: Number(order.tradeFee?.totalUsdtAmount ?? 0),
        signalFeeTotalUsdtAmount: Number(
          order.signaleFee?.totalUsdtAmount ?? 0
        ),
        tradeFeeRateBps: Number(order.tradeFeeRateBps ?? 0),
        signalFeeRateBps: Number(order.signalFeeRateBps ?? 0),
      };
    });
  }, [closedOrderList]);

  const filteredHistory = useMemo(() => closedOrders, [closedOrders]);
  const stats = useMemo(() => {
    const auto = userCexOrderTradeStats?.auto;
    const manual = userCexOrderTradeStats?.manual;
    return {
      auto: {
        totalOrders: Number(auto?.orderCount ?? 0),
        totalPnlDisplay: Number(auto?.totalPnl ?? 0),
        winRate: Number(auto?.winRate ?? 0).toFixed(1),
        totalTskxCompensation: Number(auto?.totalTskx ?? 0).toFixed(2),
      },
      manual: {
        totalOrders: Number(manual?.orderCount ?? 0),
        totalPnlDisplay: Number(manual?.totalPnl ?? 0),
        winRate: Number(manual?.winRate ?? 0).toFixed(1),
        totalTskxCompensation: Number(manual?.totalTskx ?? 0).toFixed(2),
      },
    };
  }, [userCexOrderTradeStats]);

  const openOrdersTotal =
    openOrderList?.pages?.[openOrderList.pages.length - 1]?.total ??
    runningOrders.length;
  const closedOrdersTotal =
    closedOrderList?.pages?.[closedOrderList.pages.length - 1]?.total ??
    closedOrders.length;
  const formatOrderCount = (count: number) =>
    Math.abs(count) >= 1000
      ? formatKMBTruncate(count, 0)
      : String(Math.floor(count));
  const historySyncTime = useMemo(() => {
    const last = closedOrderList?.pages?.[closedOrderList.pages.length - 1];
    if (!last) return "--:--:--";
    return new Date().toLocaleTimeString("en-GB", { hour12: false });
  }, [closedOrderList]);

  useEffect(() => {
    const onScroll = () => {
      if (lowerTab !== "orders") return;
      if (!hasNextPage || isFetchingNextPage || isOpenOrdersLoading) return;
      const threshold = 220;
      const reached =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - threshold;
      if (reached) void fetchNextPage();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isOpenOrdersLoading,
    lowerTab,
  ]);

  useEffect(() => {
    const onScroll = () => {
      if (lowerTab !== "history") return;
      if (
        !hasClosedNextPage ||
        isFetchingClosedNextPage ||
        isClosedOrdersLoading
      )
        return;
      const threshold = 220;
      const reached =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - threshold;
      if (reached) void fetchClosedNextPage();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [
    fetchClosedNextPage,
    hasClosedNextPage,
    isFetchingClosedNextPage,
    isClosedOrdersLoading,
    lowerTab,
  ]);

  return (
    <div className="px-4">
      <div
        className="relative flex items-center p-1 rounded-xl mb-4"
        style={{ background: C.card, border: `1px solid ${C.border}` }}
      >
        <motion.div
          className="absolute top-1 bottom-1 rounded-lg tab-glow-indicator"
          style={{
            width: "calc(50% - 4px)",
            background:
              "linear-gradient(180deg, rgba(34,197,94,0.14) 0%, rgba(34,197,94,0.06) 100%)",
            border: "1px solid rgba(34,197,94,0.25)",
            backdropFilter: "blur(8px)",
          }}
          animate={{ x: lowerTab === "orders" ? 4 : "calc(100% + 4px)" }}
          transition={{ type: "spring", stiffness: 380, damping: 32 }}
        />
        {(["orders", "history"] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setLowerTab(tab)}
            className="relative z-10 flex-1 py-2.5 rounded-lg text-xs font-bold transition-colors duration-200 text-center"
            style={{ color: lowerTab === tab ? C.primary : C.dim }}
          >
            {tab === "orders"
              ? t("aiTradingPage.ordersTabRunning", {
                  count: openOrdersTotal,
                })
              : t("aiTradingPage.ordersTabHistory", {
                  count: formatOrderCount(closedOrdersTotal),
                })}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {lowerTab === "orders" && (
          <motion.div
            key="orders"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="space-y-3"
          >
            {openOrderListStatus === "pending" ? (
              <div className="text-center py-12" style={{ color: C.muted }}>
                <p className="text-sm">
                  {t("aiTradingPage.openOrdersLoading")}
                </p>
              </div>
            ) : runningOrders.length === 0 ? (
              <div className="text-center py-12" style={{ color: C.muted }}>
                <BarChart2 size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">{t("aiTradingPage.noRunningOrders")}</p>
                <p className="text-[10px] mt-1" style={{ color: C.dim }}>
                  {t("aiTradingPage.noRunningOrdersHint")}
                </p>
              </div>
            ) : (
              runningOrders.map((order, i) => {
                // const isProfit = order.pnl >= 0;
                // const pnlColor = isProfit ? C.green : C.red;
                // const pnlPct =
                //   order.margin > 0
                //     ? ((order.pnlDisplay / order.margin) * 100).toFixed(1)
                //     : "0.0";
                const coinBg =
                  order.coin === "BTC"
                    ? "rgba(247,147,26,0.12)"
                    : order.coin === "ETH"
                      ? "rgba(98,126,234,0.12)"
                      : order.coin === "SOL"
                        ? "rgba(153,69,255,0.12)"
                        : "rgba(240,185,11,0.12)";
                // const currentPrice =
                //   coinPrices[order.coin]?.price ??
                //   coinPrices.BTC?.price ??
                //   order.openAmount;
                const isLong = order.type === "多";
                const dirColor = isLong ? C.green : C.red;
                const dirBorderColor = isLong
                  ? "rgba(34,197,94,0.25)"
                  : "rgba(239,68,68,0.25)";
                const dirBgColor = isLong
                  ? "rgba(34,197,94,0.04)"
                  : "rgba(239,68,68,0.04)";

                return (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <div
                      className="rounded-xl overflow-hidden p-3.5"
                      style={{
                        background: dirBgColor,
                        border: `1px solid ${dirBorderColor}`,
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                            style={{ background: coinBg }}
                          >
                            <CoinLogo coin={order.coin} size={18} />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span
                                className="font-bold text-[13px]"
                                style={{ color: C.text }}
                              >
                                {order.coin}/USDT
                              </span>
                              <DirBadge dir={order.type} />
                              <SourceBadge source={order.orderSource} />
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                              <span
                                className="text-[10px] mono-num px-1.5 py-[2px] rounded"
                                style={{
                                  background: "rgba(255,255,255,0.06)",
                                  color: C.muted,
                                  border: "1px solid rgba(255,255,255,0.04)",
                                }}
                              >
                                {order.leverage}x
                              </span>
                              <span
                                className="text-[10px] mono-num"
                                style={{ color: C.dim }}
                              >
                                {t("aiTradingPage.ordersMargin")} $
                                {formatUSDT(order.margin)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {/* 
                          <div
                            className="mono-num font-bold text-[16px] leading-tight"
                            style={{ color: pnlColor }}
                          >
                            {isProfit ? "+" : "-"}$
                            {formatUSDT(Math.abs(order.pnlDisplay))}
                          </div>
                          <div
                            className="mono-num text-[11px] mt-0.5"
                            style={{ color: pnlColor }}
                          >
                            {isProfit ? "+" : ""}
                            {pnlPct}%
                          </div>
                          <div
                            className="mono-num text-[10px] mt-1"
                            style={{ color: C.dim }}
                          >
                            $
                            {currentPrice.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </div>
                          */}
                          <div
                            className="text-[9px] uppercase tracking-wide mb-0.5"
                            style={{ color: C.dim }}
                          >
                            {t("aiTradingPage.runningStatus")}
                          </div>
                          <div
                            className="mono-num text-[12px]"
                            style={{ color: C.muted }}
                          >
                            {t("aiTradingPage.openOrderPriceLine", {
                              value: formatNumberTruncate(order.openAmount),
                            })}
                          </div>
                        </div>
                      </div>

                      <div
                        className="flex items-center justify-between mt-2 pt-2"
                        style={{ borderTop: `1px solid ${C.border}` }}
                      >
                        <span
                          className="text-[10px] mono-num flex items-center gap-1"
                          style={{ color: C.dim }}
                        >
                          <Clock size={10} className="opacity-60" />
                          {t("aiTradingPage.holdingLabel")}{" "}
                          <HoldingTimerInline openTimeMs={order.openTimeMs} />
                        </span>
                        <span
                          className="text-[9px] flex items-center gap-1 px-2 py-[3px] rounded-full"
                          style={{
                            background: "rgba(6,182,212,0.10)",
                            color: C.cyan,
                            border: "1px solid rgba(6,182,212,0.15)",
                          }}
                        >
                          <Zap size={9} />
                          {t("aiTradingPage.aiDynamicTpSl")}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => {
                            setDetailOrderId(order.id);
                            setDetailDialogOpen(true);
                          }}
                          className="flex-1 py-1.5 rounded-lg text-[10px] font-medium flex items-center justify-center gap-1 transition-all active:scale-95"
                          style={{
                            background: "rgba(255,255,255,0.04)",
                            color: C.muted,
                            border: `1px solid ${C.border}`,
                          }}
                        >
                          <Info size={10} />
                          {t("aiTradingPage.viewDetail")}
                        </button>
                        {order.orderSource === "manual" && (
                          <button
                            onClick={() => {
                              setCloseOrderId(order.id);
                              setCloseDialogOpen(true);
                            }}
                            className="flex-1 py-1.5 rounded-lg text-[10px] font-medium flex items-center justify-center gap-1 transition-all active:scale-95"
                            style={{
                              background: isLong
                                ? "rgba(34,197,94,0.08)"
                                : "rgba(239,68,68,0.08)",
                              color: dirColor,
                              border: `1px solid ${isLong ? "rgba(34,197,94,0.20)" : "rgba(239,68,68,0.20)"}`,
                            }}
                          >
                            <XCircle size={10} />
                            {t("aiTradingPage.closePosition")}
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
            {isFetchingNextPage && (
              <p className="text-center text-[11px]" style={{ color: C.dim }}>
                {t("aiTradingPage.loadMoreOpenOrders")}
              </p>
            )}
          </motion.div>
        )}

        {lowerTab === "history" && (
          <motion.div
            key="history"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            <div
              className="rounded-xl overflow-hidden"
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
              }}
            >
              <div className="flex">
                <div
                  className="flex-1 px-3 py-2.5"
                  style={{ borderRight: `1px solid ${C.border}` }}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Zap size={10} style={{ color: C.green }} />
                    <span
                      className="text-[11px] font-bold"
                      style={{ color: C.green }}
                    >
                      {t("aiTradingPage.badgeAuto")}
                    </span>
                    <span
                      className="text-[10px] mono-num ml-auto"
                      style={{ color: C.dim }}
                    >
                      {t("aiTradingPage.ordersStatsCount", {
                        count: formatOrderCount(stats.auto.totalOrders),
                      })}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span
                      className="mono-num font-bold text-[13px]"
                      style={{
                        color:
                          stats.auto.totalPnlDisplay >= 0 ? C.green : C.red,
                      }}
                    >
                      {stats.auto.totalPnlDisplay >= 0 ? "+$" : "-$"}
                      {formatUSDT(Math.abs(stats.auto.totalPnlDisplay))}
                    </span>
                    <span
                      className="text-[10px] mono-num"
                      style={{ color: C.muted }}
                    >
                      {t("aiTradingPage.ordersWinRate", {
                        rate: stats.auto.winRate,
                      })}
                    </span>
                  </div>
                  {/* {+stats.auto.totalTskxCompensation > 0 && (
                    <div
                      className="text-[9px] mono-num mt-0.5"
                      style={{ color: C.amber }}
                    >
                      +{stats.auto.totalTskxCompensation} {tskxDisplayName}
                    </div>
                  )} */}
                </div>
                <div className="flex-1 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Target size={10} style={{ color: C.blue }} />
                    <span
                      className="text-[11px] font-bold"
                      style={{ color: C.blue }}
                    >
                      {t("aiTradingPage.badgeManual")}
                    </span>
                    <span
                      className="text-[10px] mono-num ml-auto"
                      style={{ color: C.dim }}
                    >
                      {t("aiTradingPage.ordersStatsCount", {
                        count: formatOrderCount(stats.manual.totalOrders),
                      })}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span
                      className="mono-num font-bold text-[13px]"
                      style={{
                        color:
                          stats.manual.totalPnlDisplay >= 0 ? C.green : C.red,
                      }}
                    >
                      {stats.manual.totalPnlDisplay >= 0 ? "+$" : "-$"}
                      {formatUSDT(Math.abs(stats.manual.totalPnlDisplay))}
                    </span>
                    <span
                      className="text-[10px] mono-num"
                      style={{ color: C.muted }}
                    >
                      {t("aiTradingPage.ordersWinRate", {
                        rate: stats.manual.winRate,
                      })}
                    </span>
                  </div>
                  {/* {+stats.manual.totalTskxCompensation > 0 && (
                    <div
                      className="text-[9px] mono-num mt-0.5"
                      style={{ color: C.amber }}
                    >
                      +{stats.manual.totalTskxCompensation} {tskxDisplayName}
                    </div>
                  )} */}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter size={13} style={{ color: C.dim }} />
                {(["all", "auto", "manual"] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => setHistoryFilter(f)}
                    className="px-2.5 py-1 rounded text-xs font-semibold transition-all"
                    style={{
                      background:
                        historyFilter === f
                          ? "rgba(34,197,94,0.10)"
                          : "transparent",
                      color: historyFilter === f ? C.primary : C.dim,
                      border: `1px solid ${historyFilter === f ? "rgba(34,197,94,0.25)" : "transparent"}`,
                    }}
                  >
                    {f === "all"
                      ? t("aiTradingPage.ordersFilterAll")
                      : f === "auto"
                        ? t("aiTradingPage.badgeAuto")
                        : t("aiTradingPage.badgeManual")}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] mono-num" style={{ color: C.dim }}>
                  {t("aiTradingPage.ordersSyncAt", {
                    time: historySyncTime,
                  })}
                </span>
                <button
                  className="p-1 rounded"
                  style={{ color: C.dim }}
                  onClick={async () => {
                    toast.info(t("aiTradingPage.toastHistorySyncing"));
                    await Promise.all([
                      refetchClosedOrderList(),
                      refetchUserCexOrderTradeStats(),
                    ]);
                    toast.success(t("aiTradingPage.toastHistorySynced"));
                  }}
                >
                  <RotateCcw size={12} />
                </button>
              </div>
            </div>

            {closedOrderListStatus === "pending" ? (
              <div className="text-center py-12" style={{ color: C.muted }}>
                <p className="text-sm">
                  {t("aiTradingPage.closedOrdersLoading")}
                </p>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-center py-12" style={{ color: C.muted }}>
                <BarChart2 size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">{t("aiTradingPage.noHistoryOrders")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredHistory.map((order, i) => {
                  const isProfit = order.pnlDisplay >= 0;
                  const netLoss = isProfit ? 0 : Math.abs(order.pnlDisplay);
                  const pnlPct =
                    order.margin > 0
                      ? ((order.pnlDisplay / order.margin) * 100).toFixed(1)
                      : "0.0";
                  const coinBg =
                    order.coin === "BTC"
                      ? "rgba(247,147,26,0.12)"
                      : order.coin === "ETH"
                        ? "rgba(98,126,234,0.12)"
                        : order.coin === "SOL"
                          ? "rgba(153,69,255,0.12)"
                          : "rgba(240,185,11,0.12)";
                  // const hIsLong = order.type === "多";
                  const hDirBorderColor = isProfit
                    ? "rgba(34,197,94,0.25)"
                    : "rgba(239,68,68,0.25)";
                  const hDirBgColor = isProfit
                    ? "rgba(34,197,94,0.04)"
                    : "rgba(239,68,68,0.04)";
                  return (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="cursor-pointer active:scale-[0.99] transition-transform"
                      onClick={() => setHistoryDetailOrder(order)}
                    >
                      <div
                        className="rounded-xl overflow-hidden p-3.5"
                        style={{
                          background: hDirBgColor,
                          border: `1px solid ${hDirBorderColor}`,
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                              style={{ background: coinBg }}
                            >
                              <CoinLogo coin={order.coin} size={18} />
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span
                                  className="font-bold text-[13px]"
                                  style={{ color: C.text }}
                                >
                                  {order.coin}/USDT
                                </span>
                                <DirBadge dir={order.type} />
                                <SourceBadge source={order.orderSource} />
                              </div>
                              <div className="flex items-center gap-2 mt-1.5">
                                <span
                                  className="text-[10px] mono-num px-1.5 py-[2px] rounded"
                                  style={{
                                    background: "rgba(255,255,255,0.06)",
                                    color: C.muted,
                                    border: "1px solid rgba(255,255,255,0.04)",
                                  }}
                                >
                                  {order.leverage}x
                                </span>
                                <span
                                  className="text-[10px] mono-num"
                                  style={{ color: C.dim }}
                                >
                                  {t("aiTradingPage.ordersMargin")} $
                                  {formatUSDT(order.margin)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div
                              className="mono-num font-bold text-[16px] leading-tight"
                              style={{ color: isProfit ? C.green : C.red }}
                            >
                              {isProfit ? "+" : "-"}$
                              {formatUSDT(Math.abs(order.pnlDisplay))}
                            </div>
                            <div
                              className="mono-num text-[11px] mt-0.5"
                              style={{ color: isProfit ? C.green : C.red }}
                            >
                              {order.pnlDisplay >= 0 ? "+" : ""}
                              {pnlPct}%
                            </div>
                          </div>
                        </div>

                        <div
                          className="flex items-center justify-between mt-2.5 pt-2"
                          style={{ borderTop: `1px solid ${C.border}` }}
                        >
                          <span
                            className="text-[10px] mono-num"
                            style={{ color: C.dim }}
                          >
                            {order.closeTime}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span
                              className="text-[10px] mono-num"
                              style={{ color: isProfit ? C.green : C.amber }}
                            >
                              {isProfit
                                ? `+$${formatUSDT(order.closeSplitStaticUsdt)}`
                                : ""}
                            </span>
                            <ChevronRight size={12} style={{ color: C.dim }} />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
            {isFetchingClosedNextPage && (
              <p className="text-center text-[11px]" style={{ color: C.dim }}>
                {t("aiTradingPage.loadMoreHistory")}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <OrderDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setDetailOrderId(null);
        }}
        order={detailOrder}
      />
      <ClosePositionDialog
        open={closeDialogOpen}
        onClose={() => {
          setCloseOrderId(null);
          setCloseDialogOpen(false);
        }}
        order={closeOrder}
      />
      <HistoryDetailDialog
        order={historyDetailOrder}
        onClose={() => setHistoryDetailOrder(null)}
        onReorder={coin => {
          setHistoryDetailOrder(null);
          onReorderFromHistory(coin);
        }}
      />
    </div>
  );
}
